// Ukrainian (ua) admin translations.
//
// Note on locale code: project convention is `ua` (NOT BCP-47 `uk`). See
// memory/project_locale_codes.md. Only Apple Wallet uses `uk` for iOS BCP-47.
//
// Scope: backfills D2 "Build-your-own-bouquet" keys (admin form + custom-bouquet
// order-line block). Everything else inherits English so the admin remains
// usable for ua users until a full translation pass lands.
//
// Vocabulary aligned with the customer-facing builder in
// `moonflowers-app/src/i18n/ua.json` ("власний букет", "Збірка", "Оформлення",
// "Квіти"). Do not reinvent terms here without updating the mobile app too.
import customEnglishMessages from './en';

const customUkrainianMessages = {
  ...customEnglishMessages,
  resources: {
    ...customEnglishMessages.resources,
    'manual-orders': {
      ...customEnglishMessages.resources['manual-orders'],
      fields: {
        ...customEnglishMessages.resources['manual-orders'].fields,
        customRequest: 'Індивідуальний запит',
        customBudget: 'Індивідуальний бюджет',
        paidAt: 'Оплата',
        paid: 'Оплачено',
        unpaid: 'Не оплачено',
        event: 'Подія',
      },
    },
    events: {
      name: 'Подія |||| Події',
      fields: {
        title: 'Назва',
        description: 'Опис',
        deliveryStartDate: 'Початок доставки',
        deliveryEndDate: 'Кінець доставки',
        maxPerDay: 'Макс. замовлень на день',
        branchId: 'Філія',
        deliveryFee: 'Вартість доставки',
        currency: 'Валюта',
        allowedCategoryIds: 'Дозволені категорії',
        allowedProductIds: 'Дозволені букети',
        contactPhone: 'Телефон отримувача',
        contactData: 'Імʼя отримувача',
        defaultAddress: 'Адреса доставки за замовчуванням',
        isActive: 'Активна',
        createdAt: 'Створено',
        updatedAt: 'Оновлено',
      },
      sections: {
        pricing: 'Ціни',
        catalogScope: 'Склад каталогу',
        contact: 'Контакти та доставка',
      },
      helpers: {
        titleFallback: 'Чеська (основна) назва. Показується чеським гостям і використовується як запасний варіант для мов без перекладу.',
        descriptionFallback: 'Чеський (основний) опис. Показується чеським гостям і використовується як запасний варіант для мов без перекладу.',
        maxPerDay: 'Дата стає недоступною, коли на неї набрано стільки замовлень. За замовчуванням 1.',
        branch: 'Філія виконання. Обовʼязково — гостьові замовлення успадковують цю філію.',
        deliveryFee: 'Додається до кожного замовлення. Залиште порожнім для глобального налаштування.',
        catalogScope:
          'Залиште вибір букетів порожнім, щоб запропонувати всі активні букети каталогу. Інакше гості бачитимуть лише вибрані букети.',
        defaultAddress: 'Підставляється гостю, але може бути змінена під час оформлення.',
      },
      copyLink: {
        sectionLabel: 'Гостьове посилання',
        label: 'Публічний гостьовий URL',
        helper: 'Закодуйте цей URL у QR-код, надрукований для події.',
        button: 'Скопіювати посилання',
        copied: 'Гостьове посилання скопійовано',
        copyFailed: 'Не вдалося скопіювати — виділіть поле та скопіюйте вручну.',
      },
    },
  },
  app: {
    ...customEnglishMessages.app,
    board: {
      ...customEnglishMessages.app.board,
      weddingEvent: 'Весільна подія',
    },
    order: {
      ...customEnglishMessages.app.order,
      eventDetails: 'Деталі події',
    },
    mobileOrder: {
      ...customEnglishMessages.app.mobileOrder,
      updateStatus: 'Змінити статус',
      status: {
        pending: 'Очікує',
        confirmed: 'Підтверджено',
        preparing: 'Готується',
        ready: 'Готово',
        delivering: 'Доставляється',
        delivered: 'Доставлено',
        cancelled: 'Скасовано',
      },
      // D2.5 — custom-bouquet detail block
      customBouquet: {
        tag: 'ВЛАСНИЙ',
        fallbackTitle: 'Власний букет',
        tier: 'Tier %{code}',
        stems: 'Квіти',
        stemsSubtotal: 'Проміжний підсумок за квіти',
        lineTotal: 'Позиція',
        assembly: 'Збірка',
        customization: 'Оформлення',
        customizationSubtotal: 'Проміжний підсумок за оформлення',
        snapshotTotal: 'Разом за власний букет',
        snapshotMissing:
          'Знімок недоступний. Ендпоінт деталей замовлення має включати `customCompositions[%{id}]`, щоб тут відображалися повні дані.',
        wrap: 'Упаковка',
        ribbon: 'Стрічка',
        box: 'Коробка',
        card: 'Листівка',
        none: 'Немає',
      },
    },
    bouquetBuilder: {
      title: 'Налаштування конструктора букетів',
      menu: 'Конструктор букетів',
      enabled: 'Конструктор увімкнено',
      enabledHint:
        'Головний вимикач. Коли вимкнено, екран конструктора та плитка в каталозі приховані.',
      live: 'Увімкнено',
      off: 'Вимкнено',
      minOrderTitle: 'Мінімальна сума замовлення',
      minOrderInvalid: 'Мінімальна сума має бути цілим невід’ємним числом',
      tiersTitle: 'Розмірні тіри (діапазони квіток + збір за збірку)',
      tiersHint:
        'Тіри мають покривати 1..∞ без пропусків. Лише останній тір може мати ∞ як максимум.',
      saved: 'Налаштування конструктора збережено',
      lastUpdated: 'Останнє оновлення: %{date}',
      tier: {
        code: 'Код',
        minStems: 'Мін. квіток',
        maxStems: 'Макс. квіток',
        feeKc: 'Збір (CZK)',
        add: 'Додати тір',
        infinityHint: 'Перемкнути ∞ (без верхньої межі) для останнього тіру',
      },
      category: {
        title: 'Категорії — переклади + фото',
        slug: 'Внутрішня назва',
        image: 'Фото-заглушка',
        noImage: 'Без зображення',
        titles: 'Назви для кожної локалі',
        uploadButton: 'Завантажити зображення',
        uploadHint: 'JPG/PNG, ≤ 5 МБ',
        uploadPending:
          'Завантаження зображень очікує підтримки з боку backend (колонка + ендпоінт)',
        uploadOk: 'Зображення завантажено',
        saved: 'Категорії збережено',
        nothingChanged: 'Немає змін у категоріях для збереження',
        missingCs: 'Чеська (cs) назва обовʼязкова для: ',
        empty: "Не знайдено жодної категорії 'Flowers –%'. Перевірте конвенцію іменування.",
        dirty: '%{n} незбережених',
        clean: 'Усі зміни збережено',
        backendPending:
          'Завантаження зображень і збереження перекладів потребують доопрацювання backend: (1) виконати міграцію 20260524120000 (categories.image_url), (2) розширити UpdateCategoryDto, дозволивши поля `translations` і `image_url`, (3) додати POST /categories/:id/image (multipart) за зразком campaigns.',
      },
    },
  },
};

export default customUkrainianMessages;
